import { api, retryRequest } from './api';

/**
 * User Service for Team & Role Management
 *
 * Provides operations for team management within a tenant.
 * Supports role-aware access (owner, admin, member, viewer).
 */

class UserService {
  /**
   * Get the current user's role and owner status for a tenant
   * @returns {Promise<Object>} { is_owner, role, tenant_id }
   */
  async getUserStatus(authToken, tenant) {
    try {
      const response = await api.get('/v1/user/status', {
        headers: {
          'Authorization': 'Bearer ' + authToken,
          'tenant': tenant
        }
      });
      return response.data;
    } catch (error) {
      console.error('User: Error checking status:', error);
      throw error;
    }
  }

  /**
   * Get list of all users in this tenant (with roles)
   * @returns {Promise<Array>} List of users with role field
   */
  async getUsers(authToken, tenant) {
    try {
      const response = await retryRequest(async () => {
        return await api.get('/v1/user/users', {
          headers: {
            'Authorization': 'Bearer ' + authToken,
            'tenant': tenant
          }
        });
      });
      return response.data.users || [];
    } catch (error) {
      console.error('User: Error fetching users:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied. Admin or owner privileges required.');
      }
      throw error;
    }
  }

  /**
   * Invite a user to this tenant
   * @param {Object} userData - { email, name, role? }
   */
  async inviteUser(authToken, tenant, userData) {
    try {
      const response = await api.post('/v1/user/users', userData, {
        headers: {
          'Authorization': 'Bearer ' + authToken,
          'tenant': tenant
        }
      });
      return response.data;
    } catch (error) {
      console.error('User: Error inviting user:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied. Admin or owner privileges required.');
      }
      if (error.response?.status === 409) {
        throw new Error(error.response.data?.detail || 'User already exists.');
      }
      throw error;
    }
  }

  /**
   * Remove a user from this tenant
   */
  async removeUser(authToken, tenant, username) {
    try {
      const response = await api.delete(`/v1/user/users/${encodeURIComponent(username)}`, {
        headers: {
          'Authorization': 'Bearer ' + authToken,
          'tenant': tenant
        }
      });
      return response.data;
    } catch (error) {
      console.error('User: Error removing user:', error);
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.includes('cannot remove yourself')) {
          throw new Error('You cannot remove yourself from the tenant.');
        }
        if (detail?.includes('cannot remove owners')) {
          throw new Error('Admins cannot remove owners or other admins.');
        }
        throw new Error('Access denied. Admin or owner privileges required.');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found in this tenant.');
      }
      throw error;
    }
  }

  /**
   * Change a user's role in this tenant (owner-only)
   * @param {string} role - 'admin', 'member', or 'viewer'
   */
  async updateUserRole(authToken, tenant, username, role) {
    try {
      const response = await api.put(
        `/v1/user/users/${encodeURIComponent(username)}/role`,
        { role },
        {
          headers: {
            'Authorization': 'Bearer ' + authToken,
            'tenant': tenant
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('User: Error updating role:', error);
      if (error.response?.status === 403) {
        throw new Error(error.response.data?.detail || 'Owner access required to change roles.');
      }
      throw error;
    }
  }

  /**
   * Get all tenants the current user has access to, with roles
   * @returns {Promise<Array>} [{ tenant_id, tenant_name, role }]
   */
  async getMyTenants(authToken, tenant) {
    try {
      const response = await api.get('/v1/user/my-tenants', {
        headers: {
          'Authorization': 'Bearer ' + authToken,
          'tenant': tenant
        }
      });
      return response.data || [];
    } catch (error) {
      console.error('User: Error fetching tenants:', error);
      return [];
    }
  }

  /**
   * Get analytics for tenants owned by the current user
   */
  async getAnalytics(authToken, tenant) {
    try {
      const response = await retryRequest(async () => {
        return await api.get('/v1/user/analytics', {
          headers: {
            'Authorization': 'Bearer ' + authToken,
            'tenant': tenant
          }
        });
      });
      return response.data;
    } catch (error) {
      console.error('User: Error fetching analytics:', error);
      throw error;
    }
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;

// Export class for testing
export { UserService };
